import { apiBuilder, prefixApi } from "@zodios/core";
import { z } from "zod";
import { columnFilterSchema } from "../../common/columnFilterSchema";
import { statsSchema } from "../../common/statsSchema";
import { merchStatsSchema } from "./v4Query";

export const orgReportApiDefinitions = prefixApi(
  "/api",
  apiBuilder({
    alias: "getV4OrgReportByOrg",
    method: "get",
    path: "/v4/query/?table=org",
    parameters: [
      {
        name: "search",
        type: "Query",
        schema: z.string().array().optional(),
      },
      {
        name: "marketplace",
        type: "Query",
        schema: z.string(),
      },
      {
        name: "grouping",
        type: "Query",
        schema: z.string(),
      },
      {
        name: "start_date",
        type: "Query",
        schema: z.string().datetime(),
      },
      {
        name: "end_date",
        type: "Query",
        schema: z.string().datetime(),
      },
      {
        name: "start_date_previous",
        type: "Query",
        schema: z.string().datetime(),
      },
      {
        name: "end_date_previous",
        type: "Query",
        schema: z.string().datetime(),
      },
      {
        name: "filters",
        type: "Query",
        schema: z
          .array(
            z.object({
              id: z.string(),
              value: z.object({
                op: z.string().transform((op) => {
                  return op;
                }),
                value: z.unknown(),
              }),
            }),
          )
          .optional(),
      },
      {
        name: "cursor",
        type: "Query",
        schema: z.number().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    status: 200,
    response: z.object({
      data: z.array(
        z
          .object({
            org_id: z.number(),
            name: z.string(),
            market_place: z.string(),
            profile_type: z.string(),
            org_type: z.string(),
            amazon_profile__name: z.string(),
            org_is_visible: z.boolean(),
            tags: z
              .string()
              .transform((tags) => {
                try {
                  return JSON.parse(tags) as string[];
                } catch {
                  return [];
                }
              })
              .nullable(),
          })
          .merge(statsSchema)
          .merge(merchStatsSchema),
      ),
      cursor: z
        .object({
          hasPreviousPage: z.boolean(),
          hasNextPage: z.boolean(),
          startCursor: z.string().nullable(),
          endCursor: z.string().nullable(),
        })
        .nullable(),
      count: z.number(),
    }),
  })
    .addEndpoint({
      alias: "getV4OrgReportByDate",
      method: "get",
      path: "/v4/query/?table=org&grouping=start_date&limit=1000000&include_count=false",
      parameters: [
        {
          name: "search",
          type: "Query",
          schema: z.string().array().optional(),
        },

        {
          name: "marketplace",
          type: "Query",
          schema: z.string(),
        },
        {
          name: "start_date",
          type: "Query",
          schema: z.string().datetime(),
        },
        {
          name: "end_date",
          type: "Query",
          schema: z.string().datetime(),
        },
        {
          name: "start_date_previous",
          type: "Query",
          schema: z.string().datetime(),
        },
        {
          name: "end_date_previous",
          type: "Query",
          schema: z.string().datetime(),
        },
        {
          name: "date_group",
          type: "Query",
          schema: z.string(),
        },
        {
          name: "filters",
          type: "Query",
          schema: z
            .array(
              z.object({
                id: z.string(),
                value: z.object({
                  op: z.string().transform((op) => {
                    return op;
                  }),
                  value: z.unknown(),
                }),
              }),
            )
            .optional(),
        },
      ],
      status: 200,
      response: z.object({
        data: z.array(
          z
            .object({
              start_date: z.string().date(),
            })
            .merge(statsSchema)
            .merge(merchStatsSchema),
        ),
        cursor: z
          .object({
            hasPreviousPage: z.boolean(),
            hasNextPage: z.boolean(),
            startCursor: z.string().nullable(),
            endCursor: z.string().nullable(),
          })
          .nullable(),
        count: z.number(),
      }),
    })
    .addEndpoint({
      alias: "getV4OrgReportTotals",
      method: "get",
      path: "/v4/query/aggregate/?table=org",
      parameters: [
        {
          name: "search",
          type: "Query",
          schema: z.string().array().optional(),
        },
        {
          name: "marketplace",
          type: "Query",
          schema: z.string(),
        },
        {
          name: "start_date",
          type: "Query",
          schema: z.string().datetime(),
        },
        {
          name: "end_date",
          type: "Query",
          schema: z.string().datetime(),
        },
        {
          name: "start_date_previous",
          type: "Query",
          schema: z.string().datetime(),
        },
        {
          name: "end_date_previous",
          type: "Query",
          schema: z.string().datetime(),
        },
        {
          name: "filters",
          type: "Query",
          schema: z
            .array(
              z.object({
                id: z.string(),
                value: z.object({
                  op: z.string().transform((op) => {
                    return op;
                  }),
                  value: z.unknown(),
                }),
              }),
            )
            .optional(),
        },
      ],
      status: 200,
      response: z.object({
        data: statsSchema.merge(merchStatsSchema),
      }),
    })
    .addEndpoint({
      alias: "getV4OrgReportExport",
      method: "get",
      path: "/v4/query/?table=org&xls=true",
      parameters: [
        {
          name: "report_type",
          type: "Query",
          schema: z.string(),
        },
        {
          name: "marketplace",
          type: "Query",
          schema: z.string(),
        },
        {
          name: "start_date",
          type: "Query",
          schema: z.string().datetime(),
        },
        {
          name: "end_date",
          type: "Query",
          schema: z.string().datetime(),
        },
        {
          name: "filters",
          type: "Query",
          schema: columnFilterSchema,
        },
      ],
      status: 200,
      response: z.instanceof(Blob),
    })
    .addEndpoint({
      alias: "getV4OrgReportOptions",
      method: "get",
      path: "/v4/query/values/?table=org",
      parameters: [],
      status: 200,
      response: z.object({
        data: z.object({
          tags: z.array(z.string()),
        }),
      }),
    })
    .build(),
);
